import { Ref, forwardRef } from "react"
import { DateTime } from "luxon"
import { FormikHelpers, FormikProps } from "formik"
import { Formik } from "../../../../../shared/components/forms/SchemaFormik"
import * as yup from "yup"
import { StandardFormikErrorScrollWrapper } from "../../../../../shared/components/forms/StandardFormikErrorScrollWrapper"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../localization/useDateTime"
import { monetaryFieldSchema } from "../../../../../shared/components/forms/monetaryFieldValidation"
import { CreateUpdateProductFormContent } from "./CreateUpdateProductFormContent"
import {
    getProductGroupTimeInterval,
    getProductGroupWeekDays,
    isWithinGroupTimeInterval,
    isWithinGroupWeekDays,
} from "../productGroupAvailability"
import { toCreateProduct } from "../productFormValues"
import { WeekDays } from "../../productGroup/productGroupTypes"
import {
    CreateProduct,
    CreateUpdateProductFormValues,
    StockTransactionType,
    StockUpdateInventory,
} from "../productsTypes"
import { PriceListType } from "../../priceList/priceListApi"

type CreateUpdateProductFormProps = Readonly<{
    initialValues?: CreateUpdateProductFormValues | null
    defaultPriceList?: PriceListType | null
    onSubmit: (formData: CreateProduct) => void
}>
type FormikRef = Ref<FormikProps<CreateUpdateProductFormValues>>

export const CreateUpdateProductForm = forwardRef(
    ({ initialValues, defaultPriceList, onSubmit }: CreateUpdateProductFormProps, ref: FormikRef) => {
        const { l10n } = useAppLocalization()
        const dateTimeFactory = useDateTime()

        const defaultInitialValues = {
            productGroup: null,
            productNumber: "",
            name: "",
            price: undefined,
            currency: "DKK",
            shops: [],
            // priceLists: [],
            priceList: defaultPriceList ?? null,
            description: "",
            isActive: true,
            isAdminOnly: false,
            availabilityDetails: {
                weekDays: [],
                allowPreOrder: false,
            },
            variablePrice: false,
            isPriceHidden: false,
            isLocked: false,
            productGroupCode: undefined,
            itemLimits: null,
            itemsForSalePerDay: undefined,
            stock: null,
        }

        const validationSchema = yup.object({
            productGroup: yup
                .object()
                .shape({
                    id: yup.number(),
                    name: yup.string(),
                    isActive: yup.boolean(),
                })
                .required(l10n.getString("required-field")),
            productNumber: yup.string(),
            name: yup.string().required(l10n.getString("required-field")),
            price: monetaryFieldSchema("non-negative", l10n, l10n.getString("product-create-price-field-title")).when(
                "variablePrice",
                {
                    is: false,
                    then: (schema) => schema.required(l10n.getString("required-field")),
                },
            ),
            currency: yup.string(),
            shops: yup.array().of(
                yup.object().shape({
                    id: yup.number(),
                    name: yup.string(),
                    isActive: yup.boolean(),
                }),
            ),
            // priceLists: yup
            //     .array()
            //     .of(
            //         yup.object().shape({
            //             id: yup.number(),
            //             name: yup.string(),
            //             isActive: yup.boolean(),
            //         }),
            //     )
            //     .min(1, l10n.getString("required-field")),
            priceList: yup
                .object()
                .shape({
                    id: yup.number(),
                })
                .nullable()
                .required(l10n.getString("required-field")),
            //add characters limit not to let ppl send very big texts to server, maybe add that character limit to form field like we did in gopay app
            description: yup.string(),
            variablePrice: yup.boolean(),
            isPriceHidden: yup.boolean(),
            isLocked: yup.boolean(),
            isActive: yup.boolean(),
            isAdminOnly: yup.boolean(),
            itemLimits: yup
                .object({
                    maxDailyItemsPerPerson: yup
                        .number()
                        .nullable()
                        .transform((value) => (isNaN(value) ? undefined : value))
                        .min(1, l10n.getString("product-create-item-limits-max-error"))
                        .optional(),
                    minItemsPerOrder: yup
                        .number()
                        .nullable()
                        .transform((value) => (isNaN(value) ? undefined : value))
                        .min(2, l10n.getString("product-create-item-limits-min-error"))
                        .optional(),
                })
                .nullable()
                .test("either-or", l10n.getString("product-create-item-limits-error"), function (value) {
                    if (value === null || value === undefined) return true
                    return !!value.maxDailyItemsPerPerson || !!value.minItemsPerOrder
                }),
            availabilityDetails: yup
                .object({
                    dateInterval: yup
                        .object({
                            from: yup.mixed().nullable().optional(),
                            to: yup.mixed().nullable().optional(),
                        })
                        .optional(),
                    weekDays: yup.array().of(yup.mixed<WeekDays>().oneOf(Object.values(WeekDays))),
                    timeInterval: yup
                        .object({
                            start: yup.mixed().nullable().optional(),
                            end: yup.mixed().nullable().optional(),
                        })
                        .optional(),
                    allowPreOrder: yup.boolean(),
                })
                .test("date-range-valid", l10n.getString("product-create-availability-date-error"), function (value) {
                    const from = value?.dateInterval?.from
                    const to = value?.dateInterval?.to
                    if (!from || !to) return true
                    return from <= to
                })
                .test(
                    "time-range-complete",
                    l10n.getString("product-create-availability-time-incomplete-error"),
                    function (value) {
                        const start = value?.timeInterval?.start
                        const end = value?.timeInterval?.end
                        return Boolean(start) === Boolean(end)
                    },
                )
                .test("time-range-valid", l10n.getString("product-create-availability-time-error"), function (value) {
                    const start = value?.timeInterval?.start
                    const end = value?.timeInterval?.end
                    if (!start || !end) return true
                    return start < end
                })
                .test(
                    "week-days-within-group",
                    l10n.getString("product-create-availability-week-days-group-error"),
                    function (value) {
                        const weekDays = (value?.weekDays ?? []).filter((day): day is WeekDays => day !== undefined)
                        const groupWeekDays = getProductGroupWeekDays(this.parent.productGroup?.availabilityDetails)
                        return isWithinGroupWeekDays(weekDays, groupWeekDays)
                    },
                )
                .test(
                    "time-range-within-group",
                    l10n.getString("product-create-availability-time-group-error"),
                    function (value) {
                        const start = value?.timeInterval?.start
                        const end = value?.timeInterval?.end
                        if (!DateTime.isDateTime(start) || !DateTime.isDateTime(end)) return true
                        const groupTimeInterval = getProductGroupTimeInterval(
                            this.parent.productGroup?.availabilityDetails,
                            dateTimeFactory,
                        )
                        return isWithinGroupTimeInterval({ start, end }, groupTimeInterval)
                    },
                ),
            itemsForSalePerDay: yup
                .number()
                .nullable()
                .transform((value) => (isNaN(value) ? undefined : value))
                .min(1, l10n.getString("product-stock-on-sale-per-day-min-error"))
                .optional(),
            stock: yup
                .object({
                    total: yup
                        .number()
                        .nullable()
                        .transform((value) => (isNaN(value) ? undefined : value))
                        .optional(),
                    inventory: yup.mixed<StockUpdateInventory>().optional(),
                    inventoryIncomplete: yup.boolean().optional(),
                })
                .nullable()
                .test("stock-outbound-exceeded", l10n.getString("product-stock-negative-error"), function (stock) {
                    if (!stock?.inventory?.stockUpdate) return true
                    const { transactionType, quantityDelta } = stock.inventory.stockUpdate
                    if (transactionType !== StockTransactionType.DECREASE) return true
                    const total = stock.total ?? 0
                    return quantityDelta <= total
                })
                .test("stock-inventory-incomplete", l10n.getString("required-field"), function (stock) {
                    return !stock?.inventoryIncomplete
                }),
        })

        function handleSubmit(
            values: CreateUpdateProductFormValues,
            _helpers: FormikHelpers<CreateUpdateProductFormValues>,
        ) {
            onSubmit(toCreateProduct(values, dateTimeFactory))
        }

        return (
            <>
                <Formik<CreateUpdateProductFormValues>
                    enableReinitialize
                    initialValues={initialValues ? initialValues : defaultInitialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    innerRef={ref}
                    validateOnBlur={false}
                    validateOnChange={false}
                >
                    <StandardFormikErrorScrollWrapper<CreateUpdateProductFormValues>
                        children={<CreateUpdateProductFormContent isEditMode={!!initialValues} />}
                    />
                </Formik>
            </>
        )
    },
)
